import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react';
import moment from 'moment';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { IconButton } from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import './reminders.scss';
import SelectChannel from '../wizard/select-channel';
import {
    CheckboxInput,
    Heading3,
    TextLead,
    FlexDiv,
    TextDefault,
} from '@awarego-ui/components';

moment.updateLocale('en', { week: { dow: 1 } });

export default observer(
    ({
         scheduledDate,
         dueDate,
         reminders_enabled,
         onChangeRemindersEnabled,
         reminders_steps,
         onChangeReminder,
         onAddReminderStep,
         onDeleteReminderStep,
         channel,
         slackAvailable,
         teamsAvailable,
         slackConnected,
         teamsConnected,
         onChangeChannel,
         adminTimeZone,
     }) => {
        const [remindersError, setRemindersError] = useState({});

        useEffect(() => {
            setRemindersError((prev) => {
                const next = {};
                reminders_steps.forEach((_, idx) => {
                    if (prev[idx] != null) next[idx] = prev[idx];
                });
                return next;
            });
        }, [reminders_steps]);

        const getErrorText = useCallback(
            (code) => {
                switch (code) {
                    case 'maxDate':
                    case 'maxTime':
                    case 'maxDateTime':
                        return `Reminder must be set before the due date (${moment(dueDate).format(
                            `L HH:mm [${adminTimeZone}]`
                        )})`;
                    case 'minDate':
                    case 'minTime':
                    case 'minDateTime':
                        return scheduledDate
                            ? `Reminder must be set after the delivery time (${moment(scheduledDate).format(
                                `L HH:mm [${adminTimeZone}]`
                            )})`
                            : 'Reminder must be set to later than now.';
                    case 'disablePast':
                        return 'Due date cannot be set in the past';
                    case 'invalidDate':
                        return 'Your date is not valid';
                    default:
                        return '';
                }
            },
            [dueDate, scheduledDate, adminTimeZone]
        );

        const handlePickerError = (idx) => (newErr) =>
            setRemindersError((prev) => {
                if (!newErr) {
                    const { [idx]: _, ...rest } = prev;
                    return rest;
                }
                return { ...prev, [idx]: newErr };
            });

        const handleDelete = (reminderIdx, reminder) => {
            onDeleteReminderStep(reminder);
            setRemindersError((prev) => {
                const next = {};
                Object.entries(prev).forEach(([key, val]) => {
                    const idx = Number(key);
                    if (idx < reminderIdx) next[idx] = val;
                    else if (idx > reminderIdx) next[idx - 1] = val;
                });
                return next;
            });
        };

        return (
            <FlexDiv column mt={40}>
                <CheckboxInput
                    checked={reminders_enabled}
                    onChange={onChangeRemindersEnabled}
                    name="sendReminders"
                    label={<TextLead bold>Send reminders</TextLead>}
                    noYPadding
                />

                {reminders_enabled && (
                    <Fragment>
                        {reminders_steps.length > 0 && (
                            <Heading3 displayBlock mt={16} mb={8}>
                                Remind on
                            </Heading3>
                        )}

                        {reminders_steps.map((reminder, i) => {
                            const errorCode = remindersError[i];
                            const helperText = getErrorText(errorCode);

                            return (
                                <div className="assessment-reminder-row" key={reminder.id}>
                                    <div className="reminder-row-count">{i + 1}.</div>

                                    {reminder.alreadySet && moment(reminder.send).isBefore(moment()) ? (
                                        <span className="reminder-row-count">
                      {moment(reminder.send).format(`L HH:mm [${adminTimeZone}]`)}
                    </span>
                                    ) : (
                                        <LocalizationProvider dateAdapter={AdapterMoment}>
                                            <DateTimePicker
                                                hiddenLabel
                                                value={reminder.send ? moment(reminder.send) : null}
                                                onChange={(date) => onChangeReminder(date, i, reminder)}
                                                onError={handlePickerError(i)}
                                                ampm={false}
                                                disablePast
                                                dayOfWeekFormatter={(d) => `${d}`}
                                                format={`DD/MM/YYYY HH:mm[${adminTimeZone}]`}
                                                views={['day', 'hours', 'minutes']}
                                                minDateTime={scheduledDate ? moment(scheduledDate) : undefined}
                                                maxDateTime={dueDate ? moment(dueDate) : undefined}
                                                slotProps={{
                                                    textField: {
                                                        placeholder: 'Select date & time',
                                                        helperText,
                                                        error: Boolean(errorCode),
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    )}

                                    {!(reminder.alreadySet && moment(reminder.send).isBefore(moment())) && (
                                        <IconButton
                                            aria-label="delete"
                                            onClick={() => handleDelete(i, reminder)}
                                            color="secondary"
                                            size="large"
                                        >
                                            <DeleteForeverIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </div>
                            );
                        })}

                        {reminders_steps.length === 0 && <br />}
                        {reminders_steps.length < 5 && (
                            <TextDefault link themed bold onClick={onAddReminderStep}>
                                + Add new reminder
                            </TextDefault>
                        )}

                        <SelectChannel
                            channel={channel}
                            slackAvailable={slackAvailable}
                            teamsAvailable={teamsAvailable}
                            slackConnected={slackConnected}
                            teamsConnected={teamsConnected}
                            onChangeChannel={onChangeChannel}
                            isReminders
                        />
                    </Fragment>
                )}
            </FlexDiv>
        );
    }
);
